import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Textarea,
} from "@repo/ui";
import { ArrowLeftIcon, CheckIcon, XCircleIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Expo48Form, { type Expo48FormData } from "@/components/expo-48-form";
import QRCode from "@/components/qr-code";
import { formatNumberWithCommas, stripCommasToNumber } from "@/lib/expo-48-constants";
import {
  useCreatePropertyPurchase,
  useDeletePropertyPurchase,
  useUpdatePropertyPurchase,
} from "@/lib/queries/use-property-purchases";
import {
  useRegistrationDetail,
  useUpdateRegistration,
  useUpdateRegistrationGift,
  useUpdateRegistrationRemarks,
} from "@/lib/queries/use-registration";
import AdminNav from "@/pages/admin/admin-nav";

export default function RegistrationDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const itemId = params.id; // Can be UUID, legacy_uid, or numeric ID

  const { data, isLoading, refetch } = useRegistrationDetail(itemId);
  const updateGift = useUpdateRegistrationGift();
  const updateRegistration = useUpdateRegistration();
  const updateRemarks = useUpdateRegistrationRemarks();
  const createPropertyPurchase = useCreatePropertyPurchase();
  const updatePropertyPurchase = useUpdatePropertyPurchase();
  const deletePropertyPurchase = useDeletePropertyPurchase();
  const [saving, setSaving] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [defaultValues, setDefaultValues] = useState<Partial<Expo48FormData> | undefined>(
    undefined
  );
  const [initialPurchaseIds, setInitialPurchaseIds] = useState<number[]>([]);

  const current = data?.item;

  useEffect(() => {
    document.title = "Registration Detail | House & Condo Expo";
  }, []);

  // Initialize form when data loads
  useEffect(() => {
    if (current) {
      const purchases =
        current.property_purchases && current.property_purchases.length > 0
          ? current.property_purchases.map((pp) => ({
              id: pp.id as unknown as number,
              purchasePrice: formatNumberWithCommas(pp.purchase_price),
              developer: pp.developer || "",
              fullAddress: pp.full_address || "",
              purchasedPropertyType: pp.purchased_property_type || "",
              purchasedPropertyCharacteristics: pp.purchased_property_characteristics || "",
            }))
          : [];

      setDefaultValues({
        name: current.name || "",
        surname: current.surname || "",
        phone: current.phone || "",
        email: current.email || "",
        gender: (current.gender as unknown as string) || "",
        locationData: current.expoData.locationData || { province: "", district: "" },
        age: current.age?.toString() || "",
        interests: current.expoData.interests || [],
        interestOther: current.expoData.interestOther || "",
        banks: current.expoData.banks || [],
        bankOther: current.expoData.bankOther || "",
        propertyCharacteristics: current.expoData.propertyCharacteristics || "",
        priceRange: current.expoData.priceRange || "",
        purchaseYear: current.expoData.purchaseYear || "",
        purchases,
      });
      setRemarks(current.remarks || "");
      setInitialPurchaseIds(
        current.property_purchases?.map((pp) => pp.id as unknown as number) || []
      );
    }
  }, [current]);

  const handleSave = async (values: Expo48FormData) => {
    if (!current) return;

    setSaving(true);
    try {
      // Update basic registration data
      await updateRegistration.mutateAsync({
        id: current.id,
        name: values.name,
        surname: values.surname,
        phone: values.phone,
        email: values.email,
        gender: values.gender || null,
        locationData:
          values.locationData && values.locationData.province
            ? { province: values.locationData.province, district: values.locationData.district }
            : null,
        age: values.age ? Number(values.age) : null,
        interests: values.interests,
        interestOther: values.interestOther || null,
        banks: values.banks,
        bankOther: values.bankOther || null,
        propertyCharacteristics: values.propertyCharacteristics || null,
        priceRange: values.priceRange || null,
        purchaseYear: values.purchaseYear || null,
      });

      // Handle property purchases
      if (values.purchases && values.purchases.length > 0) {
        const currentPurchaseIds = values.purchases
          .filter((p) => p.id !== undefined)
          .map((p) => p.id as number);

        // Delete removed purchases
        const deletedIds = initialPurchaseIds.filter((id) => !currentPurchaseIds.includes(id));
        for (const id of deletedIds) {
          await deletePropertyPurchase.mutateAsync(id);
        }

        // Update or create purchases
        for (const purchase of values.purchases) {
          const purchasePrice = stripCommasToNumber(purchase.purchasePrice || "0");

          if (purchase.id) {
            // Update existing purchase
            await updatePropertyPurchase.mutateAsync({
              id: purchase.id,
              purchasePrice,
              developer: purchase.developer,
              fullAddress: purchase.fullAddress,
              purchasedPropertyType: purchase.purchasedPropertyType || undefined,
              purchasedPropertyCharacteristics:
                purchase.purchasedPropertyCharacteristics || undefined,
            });
          } else {
            // Create new purchase
            await createPropertyPurchase.mutateAsync({
              registrationId: current.id,
              purchasePrice,
              developer: purchase.developer || "",
              fullAddress: purchase.fullAddress || "",
              purchasedPropertyType: purchase.purchasedPropertyType || undefined,
              purchasedPropertyCharacteristics:
                purchase.purchasedPropertyCharacteristics || undefined,
            });
          }
        }
      }

      // Refetch to get the latest property purchase IDs
      await refetch();
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRemarks = async () => {
    if (!current) return;
    await updateRemarks.mutateAsync({
      id: current.id,
      remarks: remarks || null,
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminNav />
      <div className="container mx-auto py-8 px-4">
        <div className="mb-6">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeftIcon className="size-4" />
            Back
          </Button>
        </div>

        {current && current.legacy_uid && (
          <Alert className="mb-6">
            <AlertDescription>ลงทะเบียนล่วงหน้า</AlertDescription>
          </Alert>
        )}

        {isLoading ? (
          <Card>
            <CardContent className="py-8">
              <div className="text-center">Loading...</div>
            </CardContent>
          </Card>
        ) : current && defaultValues ? (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Registration #{itemId}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-col items-center gap-3 pb-4 border-b">
                  <QRCode uuid={current.uuid} size={192} />
                  <div className="text-sm text-muted-foreground">
                    UUID: {current.uuid}
                    {current.legacy_uid && (
                      <span className="block text-xs">Legacy UID: {current.legacy_uid}</span>
                    )}
                  </div>
                </div>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">เวลาลงทะเบียน</TableCell>
                      <TableCell>{new Date(current.created_at).toLocaleString()}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">ข้อมูลครบสำหรับคูปอง</TableCell>
                      <TableCell>
                        {current.coupon_ready_at
                          ? new Date(current.coupon_ready_at).toLocaleString()
                          : "No / ไม่"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">คูปองพิมพ์แล้ว</TableCell>
                      <TableCell>
                        {current.coupons_printed_at
                          ? new Date(current.coupons_printed_at).toLocaleString()
                          : "Not printed / ยังไม่ได้พิมพ์"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">ของรางวัล</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-4 flex-wrap">
                          <span>
                            {current.received_gift_at
                              ? `รับแล้วตอน ${new Date(current.received_gift_at).toLocaleString()}`
                              : "ยังไม่ได้รับ"}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={saving || updateGift.isPending}
                            onClick={async () => {
                              setSaving(true);
                              try {
                                await updateGift.mutateAsync({
                                  id: current.id,
                                  received_gift: !current.received_gift_at,
                                });
                              } finally {
                                setSaving(false);
                              }
                            }}
                          >
                            {saving || updateGift.isPending ? (
                              "Saving..."
                            ) : current.received_gift_at ? (
                              <>
                                <XCircleIcon className="size-4" /> {'เปลี่ยนเป็น "ยังไม่ได้รับ"'}
                              </>
                            ) : (
                              <>
                                <CheckIcon className="size-4" /> {'เปลี่ยนเป็น "รับแล้ว"'}
                              </>
                            )}
                          </Button>
                        </span>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <div className="space-y-2 pt-4 border-t">
                  <Label htmlFor="remarks">Remarks</Label>
                  <Textarea
                    id="remarks"
                    placeholder="ทิ้งรีมาร์คหรือโน้ต สำหรับคนนี้..."
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    rows={4}
                    className="resize-none"
                  />
                  <Button size="sm" onClick={handleSaveRemarks} disabled={updateRemarks.isPending}>
                    {updateRemarks.isPending ? "Saving..." : "Save Remarks"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Edit Registration</CardTitle>
              </CardHeader>
              <CardContent>
                <Expo48Form
                  mode="admin"
                  requireCouponFields={false}
                  defaultValues={defaultValues}
                  onSubmit={handleSave}
                  propertyPurchasesMetadata={
                    current.property_purchases?.map((pp) => ({
                      id: Number(pp.id),
                      coupon_ready_at: pp.coupon_ready_at ? String(pp.coupon_ready_at) : null,
                      coupons_printed_at: pp.coupons_printed_at
                        ? String(pp.coupons_printed_at)
                        : null,
                    })) || []
                  }
                >
                  <Button type="submit" disabled={saving || updateRegistration.isPending}>
                    {saving || updateRegistration.isPending ? "Saving..." : "Save Changes"}
                  </Button>
                </Expo48Form>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card>
            <CardContent className="py-8">
              <div className="text-center">No data found</div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
